import {
  Text,
  Pressable,
  VStack,
  HStack,
  Badge,
  Chip,
} from '@ayble-health/dashboard-components';
import { useState } from 'react';
import { ChevronDownIcon, FlagIcon } from 'lucide-react';
import { Box, Flex, Separator, useToken } from '@chakra-ui/react';
import {
  Medication,
  Prescription,
  PrescriptionMedicationResourceType,
  PrescriptionMedicationStatus,
} from 'api/types';
import { PrescriptionDetails } from 'components/UserDashboard/overview/PrescriptionDetails';
import { MedicationMenu } from 'components/UserDashboard/overview/MedicationMenu';
import { formatDate } from 'components/UserDashboard/utils';

type Props = {
  item: Medication | Prescription;
  isMenuOpen: boolean;
  onOpenMenuChange: (isOpen: boolean) => void;
};

export const PharmacyItemRow = ({
  item,
  isMenuOpen,
  onOpenMenuChange,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [seafoam, steel] = useToken('colors', ['seafoam', 'steel']);
  const { comment, dosage, directions, status, resource_type } = item;
  const isPrescription =
    resource_type === PrescriptionMedicationResourceType.Prescription;
  const name = isPrescription ? item.display_name : item.name;
  const dateText = isPrescription
    ? `Prescribed ${formatDate(item.date_written ?? undefined, 'MMM D, YYYY')}`
    : `Started ${formatDate(item.start_date ?? undefined, 'MMM D, YYYY')}`;

  return (
    <Box position={'relative'}>
      <Pressable
        flex={1}
        w={'100%'}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <VStack
          w={'100%'}
          borderWidth={1}
          p={3}
          borderRadius={'md'}
          gap={2}
          transition={'border-color 0.2s ease'}
          _hover={{ borderColor: 'sage' }}
        >
          <HStack
            w={'100%'}
            justifyContent={'space-between'}
            alignItems={'flex-start'}
            gap={2}
            // Reserve space so the badge/chip don't sit under the
            // absolutely-positioned menu button rendered outside this button.
            pr={12}
          >
            <VStack alignItems={'flex-start'}>
              <HStack gap={1} alignItems={'stretch'}>
                <Text color={'evergreen'} fontWeight={'semibold'}>
                  {name}
                </Text>
                <Flex
                  alignItems={'center'}
                  transform={expanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                  transition={'transform 0.2s ease'}
                >
                  <ChevronDownIcon size={16} color={steel} />
                </Flex>
              </HStack>

              <Text
                color={'bluegrey'}
                fontWeight={'normal'}
                fontFamily={'body'}
                fontSize={'xs'}
              >
                {dosage}
              </Text>
              <Text
                color={'bluegrey'}
                fontWeight={'normal'}
                fontFamily={'body'}
                fontSize={'xs'}
              >
                {dateText}
              </Text>
            </VStack>

            {status !== PrescriptionMedicationStatus.Active && (
              <HStack alignItems={'center'} gap={2} h={10}>
                <Badge variant={'negative'}>{status}</Badge>
                {isPrescription && (
                  <Chip alignSelf={'center'} variant={'mint'}>
                    {'E-prescribed'}
                  </Chip>
                )}
              </HStack>
            )}
          </HStack>

          {expanded && (
            <VStack alignItems={'flex-start'} w={'100%'} gap={3}>
              <Separator w={'100%'} my={1} />

              {directions && (
                <HStack
                  bg={'mint'}
                  rounded={'md'}
                  w={'100%'}
                  p={3}
                  gap={2}
                  alignItems={'flex-start'}
                >
                  <HStack alignItems={'center'} h={'18px'}>
                    <FlagIcon size={13} color={seafoam} />
                  </HStack>
                  <VStack alignItems={'flex-start'} gap={0}>
                    <Text
                      fontSize={'xs'}
                      fontWeight={'semibold'}
                      fontFamily={'body'}
                      textTransform={'uppercase'}
                      color={'seafoam'}
                    >
                      Instructions
                    </Text>
                    <Text
                      fontSize={'xs'}
                      color={'evergreen'}
                      fontWeight={'normal'}
                    >
                      {directions}
                    </Text>
                  </VStack>
                </HStack>
              )}

              {comment && (
                <VStack pl={3} borderLeftWidth={2} borderLeftColor={'cloud'}>
                  <Text
                    fontSize={'xs'}
                    fontWeight={'semibold'}
                    textTransform={'uppercase'}
                    color={'bluegrey'}
                  >
                    Clinical Note
                  </Text>
                  <Text
                    fontSize={'xs'}
                    color={'evergreen'}
                    fontWeight={'normal'}
                  >
                    {comment}
                  </Text>
                </VStack>
              )}

              {isPrescription && <PrescriptionDetails prescription={item} />}

              {!directions && !comment && (
                <Text fontSize={'xs'} color={'steel'}>
                  No additional details recorded
                </Text>
              )}
            </VStack>
          )}
        </VStack>
      </Pressable>

      {!isPrescription && (
        <Box position={'absolute'} top={3} right={3} zIndex={1}>
          <MedicationMenu
            medication={item}
            isOpen={isMenuOpen}
            onOpenChange={onOpenMenuChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default PharmacyItemRow;
